import * as React from "react";

import { asString, decomposeComponentID } from "common/components/component-helpers";
import { LOADING, ComponentHighlight, asUI, AppEnvironment } from "common/components/types";
import { GlideFunctionalRenderer } from "common/components/uis";
import ReactDOM from "react-dom";
import { SelectMode, withSelectMode } from "sharedUI/lib/editor-select-mode-utils";
import { styled } from "webapp/styled-components";
import { connect } from "react-redux";
import { RootState, SelectedComponentPath } from "store/types";
import { setSelectedComponent } from "reducers/uistate";
import classNames from "classnames";
import { maybe } from "common/Support";
import { lazilyLoadedComponent } from "lib/react-lazy-loading";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { app } from "routes";
import { massageCloudURL } from "common/components/portable-renderers";
import { handleFileUpload } from "sharedUI/lib/upload-utils";
import { extractDropContents } from "sharedUI/lib/drop-utils";
import { withTheme } from "styled-components";
import { RuntimeTheme } from "webapp/lib/theme/default-theme";
import { GLIDE_TABLE_COLUMN_DATA_FORMAT, GLIDE_COMPONENT_INDEX_DATA_FORMAT } from "sharedUI/lib/constants";
// eslint-disable-next-line glide/webapp-must-not-import-design
import { withAppID } from "designer/lib/with-app-id";
import { useForce } from "hooks/use-force";
import { useForceValueProducers } from "hooks/use-value-producers";
import { ErrorBoundary } from "lib/error-boundary";
import { areComponentIndexesEqual } from "common/generator/description-utils";
import { ComponentIndexes } from "common/component-indexes";

const ComponentContextMenu = lazilyLoadedComponent(
    "context-menu",
    () => import("sharedUI/ui/smart/component-context-menu/component-context-menu")
);

interface Props extends RouteComponentProps {
    readonly appEnvironment: AppEnvironment;
    readonly isHighlighted: boolean;
    readonly screenName: string;
    readonly componentIndex: ComponentIndexes;
    readonly setFlag: (flag: ComponentHighlight) => void;
    readonly selectMode: SelectMode;
    readonly needsGrow: GrowType;

    readonly appID: string | undefined;
    readonly selectedComponent: SelectedComponentPath | undefined;
    readonly setSelectedComponent: typeof setSelectedComponent;
    readonly theme: RuntimeTheme;
}

interface State {
    readonly scrollNode: Element | null;
    readonly highlighted: boolean;
    readonly rightClickMenu: [number, number] | undefined;
    readonly dragOver: boolean;
}

const HighlightStyle = styled.div`
    position: relative;

    display: flex;
    flex-direction: column;

    &.flex-contents {
        flex: 1;
    }

    .focused &.selected,
    .focused &.highlight,
    &.edit:hover {
        ::after {
            content: "";
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;

            border: 2px solid #2b5fe088;

            pointer-events: none;
        }

        opacity: 1;
    }

    &.grow-last:last-child {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
    }

    &.grow-always {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
    }

    .focused &&&&&.selected {
        ::after {
            border: 1px solid #2b5fe0;
        }
    }

    &.edit {
        * {
            pointer-events: none !important;
        }
    }

    &.drag-over {
        ::after {
            content: "";
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            border-top: 2px dashed #2b5fe0;

            pointer-events: none;
        }
    }

    .selection-active & {
        opacity: 0.5;
    }

    transition: opacity 200ms;
`;

class HighlightControllerImpl extends React.PureComponent<React.PropsWithChildren<Props>, State> {
    public state: State = {
        scrollNode: null,
        highlighted: false,
        rightClickMenu: undefined,
        dragOver: false,
    };

    private wrapperRef = React.createRef<HTMLDivElement>();

    private getScrollParent(node: Element | null): Element | null {
        if (node === null) {
            return null;
        }

        if (node.scrollHeight > node.clientHeight) {
            return node;
        } else {
            return this.getScrollParent(node.parentElement);
        }
    }

    private onEnter = () => {
        this.props.setFlag(ComponentHighlight.Designer);
    };

    private onLeave = () => {
        this.props.setFlag(ComponentHighlight.None);
    };

    private onClickCapture = (e: React.MouseEvent) => {
        const { selectMode, appID, screenName, componentIndex } = this.props;

        if (selectMode === "edit") {
            if (appID !== undefined) {
                this.props.history.push(app(appID));
                this.props.setSelectedComponent({ appID, screenName, indexes: componentIndex });
            }
            e.preventDefault();
            e.stopPropagation();
        }
    };

    private onContextMenu = (e: React.MouseEvent) => {
        const { appID, screenName, componentIndex } = this.props;
        e.preventDefault();
        e.stopPropagation();

        const targetY = e.pageY;
        const targetX = e.pageX;

        this.setState({
            rightClickMenu: [targetX, targetY],
        });

        if (appID !== undefined) {
            this.props.setSelectedComponent({ appID, screenName, indexes: componentIndex });
        }
    };

    private onCloseMenu = () => {
        this.setState({ rightClickMenu: undefined });
    };

    private getRestPosition(scroller: Element | null): { left: number; centerY: number } | undefined {
        let node = ReactDOM.findDOMNode(this.wrapperRef.current);
        if (node === null) {
            return undefined;
        }

        node = node as Element;
        const bounds = node.getBoundingClientRect();
        const nodeCenter = bounds.top + bounds.height / 2;
        const rightMarginString = window.getComputedStyle(node).marginRight;
        const matches = (rightMarginString ?? "").match(/\d+/);
        let rightMargin = 0;
        if (matches !== null) {
            rightMargin = parseInt(matches[0], 10);
        }

        if (scroller === null) {
            return {
                left: rightMargin + bounds.right + 8,
                centerY: nodeCenter,
            };
        }

        const scrollTop = scroller.scrollTop;
        const minScroll = -scrollTop;
        const maxScroll = scroller.scrollHeight - (scrollTop + scroller.clientHeight);

        const scrollerBounds = scroller.getBoundingClientRect();
        const scrollCenter = scrollerBounds.top + scrollerBounds.height / 2;

        const scrollDistance = Math.min(maxScroll, Math.max(minScroll, nodeCenter - scrollCenter));

        const finalY = nodeCenter - scrollDistance;

        return {
            left: rightMargin + bounds.right + 8,
            centerY: finalY,
        };
    }

    private scrollIntoView() {
        if (this.wrapperRef.current === null) return;
        const bounding = this.wrapperRef.current.getBoundingClientRect();
        const scrollNode = this.getScrollParent(this.wrapperRef.current);

        if (scrollNode !== null) {
            const scrollBounding = scrollNode.getBoundingClientRect();
            let scrollTopPadding = maybe(
                () => parseFloat(window.getComputedStyle(scrollNode, null).getPropertyValue("padding-top")),
                0
            );
            scrollTopPadding -= this.props.theme.navBar.largeTitleHeight;
            const scrollBottomPadding = maybe(
                () =>
                    parseFloat(
                        window
                            .getComputedStyle(scrollNode.firstChild as Element, null)
                            .getPropertyValue("padding-bottom")
                    ),
                0
            );
            const selfLocalY = bounding.top - scrollBounding.top + scrollNode.scrollTop;

            const minVisibleY = Math.max(0, selfLocalY - scrollBounding.height + scrollBottomPadding + bounding.height);
            const maxVisibleY = selfLocalY - scrollTopPadding;
            let scrollTarget = minVisibleY;
            if (bounding.height < 600) {
                scrollTarget = Math.max(minVisibleY, Math.min(maxVisibleY, scrollNode.scrollTop));
            }
            scrollNode.scrollTo({
                top: scrollTarget,
                behavior: "smooth",
            });
        }
    }

    private onSelected() {
        this.scrollIntoView();
    }

    private onHighlight() {
        if (this.wrapperRef.current === null) return;

        const parent = this.wrapperRef.current.parentElement;
        parent?.classList.add("selection-active");

        this.setState({ highlighted: true });
        const scrollNode = this.getScrollParent(this.wrapperRef.current);

        this.setState({ scrollNode });
        const rest = this.getRestPosition(scrollNode);
        if (rest === undefined) return;

        setTimeout(() => {
            if (this.state.highlighted) {
                if (this.props.selectMode === "edit") return;
                this.scrollIntoView();
            }
        }, 200);
    }

    private onUnhighlight() {
        this.setState({ highlighted: false });
        this.setState({ scrollNode: null });

        if (this.wrapperRef.current === null) return;

        const parent = this.wrapperRef.current.parentElement;
        parent?.classList.remove("selection-active");
    }

    public componentDidUpdate(prevProps: Props) {
        if (!prevProps.isHighlighted && this.props.isHighlighted) {
            this.onHighlight();
        } else if (prevProps.isHighlighted && !this.props.isHighlighted) {
            this.onUnhighlight();
        } else if (
            !areComponentIndexesEqual(prevProps.selectedComponent?.indexes, this.props.selectedComponent?.indexes) &&
            areComponentIndexesEqual(this.props.selectedComponent?.indexes, this.props.componentIndex) &&
            this.props.selectedComponent?.screenName === this.props.screenName
        ) {
            this.onSelected();
        }
    }

    private highlightProps = {
        onMouseEnter: this.onEnter,
        onMouseLeave: this.onLeave,
        onClickCapture: this.onClickCapture,
        onContextMenu: this.onContextMenu,
        ref: this.wrapperRef,
    };

    private onImageDropped = (image: string) => {
        const { appEnvironment, screenName, componentIndex } = this.props;
        appEnvironment.addImageComponent(screenName, componentIndex, image);
    };

    private onColumnDropped = (columnName: string) => {
        const { appEnvironment, screenName, componentIndex } = this.props;
        appEnvironment.addComponentForColumn(screenName, componentIndex, columnName);
    };

    private onDragOver = (ev: React.DragEvent<HTMLElement>) => {
        if (ev.dataTransfer.types.includes(GLIDE_COMPONENT_INDEX_DATA_FORMAT)) return;

        ev.stopPropagation();
        ev.preventDefault();

        if (!this.state.dragOver) {
            this.setState({ dragOver: true });
        }
    };

    private async onFileDropped(file: File): Promise<void> {
        if (this.props.appID === undefined) return;
        const url = await handleFileUpload(file, this.props.appID);
        if (url !== undefined) {
            this.onImageDropped(url);
        }
    }

    private onURIDropped(uri: string): void {
        uri = massageCloudURL(uri);
        this.onImageDropped(uri);
    }

    private onDragLeave = () => {
        this.setState({ dragOver: false });
    };

    private onDrop = async (ev: React.DragEvent<HTMLElement>) => {
        if (ev.dataTransfer.types.includes(GLIDE_COMPONENT_INDEX_DATA_FORMAT)) return;

        this.setState({ dragOver: false });

        try {
            if (ev.dataTransfer.types.includes(GLIDE_TABLE_COLUMN_DATA_FORMAT)) {
                ev.stopPropagation();
                ev.preventDefault();

                this.onColumnDropped(ev.dataTransfer.getData(GLIDE_TABLE_COLUMN_DATA_FORMAT));
            } else {
                ev.stopPropagation();
                ev.preventDefault();

                const dropContents = await extractDropContents(ev);
                if (dropContents === undefined) return;

                this.props.appEnvironment.startBuilderBusy();

                if (dropContents.kind === "file") {
                    await this.onFileDropped(dropContents.value[0]);
                } else {
                    await this.onURIDropped(dropContents.value);
                }
            }
        } finally {
            this.props.appEnvironment.stopBuilderBusy();
        }
    };

    public render(): React.ReactNode {
        const { rightClickMenu, highlighted } = this.state;
        const { screenName, componentIndex, selectedComponent, appID, selectMode, needsGrow } = this.props;

        const isSelected =
            appID === selectedComponent?.appID &&
            screenName === selectedComponent?.screenName &&
            areComponentIndexesEqual(componentIndex, selectedComponent?.indexes);

        return (
            <>
                <HighlightStyle
                    {...this.highlightProps}
                    onDragOver={this.onDragOver}
                    onDragLeave={this.onDragLeave}
                    onDrop={this.onDrop}
                    className={classNames(
                        "highlight-view",
                        this.state.dragOver && "drag-over",
                        selectMode,
                        isSelected && selectMode === "edit" ? "selected" : "",
                        highlighted && "highlight",
                        `grow-${needsGrow}`
                    )}>
                    {this.props.children}
                </HighlightStyle>
                {rightClickMenu !== undefined && (
                    <ErrorBoundary>
                        <React.Suspense fallback={<div />}>
                            <ComponentContextMenu
                                targetX={rightClickMenu[0]}
                                targetY={rightClickMenu[1]}
                                onClose={this.onCloseMenu}
                                screenName={screenName}
                                componentIndex={componentIndex}
                            />
                        </React.Suspense>
                    </ErrorBoundary>
                )}
            </>
        );
    }
}

const mapState = (rootState: RootState) => ({
    selectedComponent: rootState.uiState.selectedComponent,
});

const mapDispatch = {
    setSelectedComponent: setSelectedComponent,
};

const HighlightController = connect(
    mapState,
    mapDispatch
)(withAppID(withTheme(withSelectMode(withRouter(HighlightControllerImpl)))));

type GrowType = "never" | "always" | "last";
type GrowCallback = (grow: GrowType) => void;
export const HighlightGrowContext = React.createContext<GrowCallback>(() => undefined);

export const HighlightComponentMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [[childValue, componentIDValue], [childVP]] = useForceValueProducers(getValueProducers, force, ctx);

    const [grow, setGrow] = React.useState<GrowType>("never");
    const onSetGrow = React.useCallback(
        (newValue: GrowType) => {
            if (grow === newValue) return;
            window.requestAnimationFrame(() => {
                setGrow(newValue);
            });
        },
        [grow]
    );

    React.useEffect(() => {
        setGrow("never");
    }, [childVP]);

    if (childValue === LOADING) {
        return null;
    }

    const child = asUI(childValue).render(ctx, env);
    if (componentIDValue === LOADING) return child;

    const componentIDString = asString(componentIDValue);
    const componentID = decomposeComponentID(componentIDString);
    if (componentID === undefined) return null;

    const flagVP = env.appEnvironment.getHighlightComponentFlag(componentIDString);
    const flag = force(flagVP);

    return (
        <HighlightGrowContext.Provider value={onSetGrow}>
            <HighlightController
                appEnvironment={env.appEnvironment}
                screenName={componentID.screenName}
                componentIndex={componentID.indexes}
                needsGrow={grow}
                isHighlighted={flag === ComponentHighlight.App}
                setFlag={v => flagVP.set(v)}>
                {child}
            </HighlightController>
        </HighlightGrowContext.Provider>
    );
};
